import { Link } from "react-router-dom";
import { useState } from "react";
import { Eye, ExternalLink, Download, FileText, Film, X } from "lucide-react";
import { formatUnderscoreToSpace } from "../../utils/text-format";

export const FileDownloadView = ({ label = "", url }) => {
  const [previewMedia, setPreviewMedia] = useState(null);

  if (!url) return null;

  // Extract valid string URL regardless of data shape
  const getUrlString = (item) => {
    if (typeof item === "string") return item;
    if (typeof item === "object" && item !== null)
      return item.url || item.path || "";
    return "";
  };

  // Resolve display file name
  const getFileName = (item) => {
    if (typeof item === "object" && item !== null && item.name) {
      return item.name;
    }

    const urlStr = getUrlString(item);
    if (!urlStr) return "Download File";

    return urlStr.substring(urlStr.lastIndexOf("/") + 1) || "Download File";
  };

  // Resolve file extension category (image, video, document)
  const getFileType = (fileName, fileUrl) => {
    const target = (fileName || fileUrl || "").toLowerCase();
    if (
      /\.(apng|avif|gif|jpg|jpeg|jfif|pjpeg|pjp|png|svg|webp|bmp|ico)$/i.test(
        target,
      )
    ) {
      return "image";
    }
    if (/\.(mp4|webm|ogg|mov|m4v|mkv|avi)$/i.test(target)) {
      return "video";
    }
    const extMatch = target.match(/\.([a-z0-9]+)(\?.*)?$/i);
    return extMatch ? extMatch[1] : "file";
  };

  // Format file size if provided in object
  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return null;
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  const fileUrl = getUrlString(url);
  const fileName = getFileName(url);
  const fileSize = typeof url === "object" ? url.size : null;
  const fileCategory = getFileType(fileName, fileUrl);

  if (!fileUrl) return null;

  return (
    <div>
      <label className="text-sm font-semibold text-gray-800 capitalize">
        {formatUnderscoreToSpace(label)}
      </label>
      <div className="group relative rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition hover:shadow-md flex items-center justify-between gap-3w-full max-w-[30rem]">
        <p className="mb-2 text-sm font-medium text-gray-500 dark:text-gray-400 capitalize"></p>
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Preview Thumbnail / Icon */}
          <div className="relative flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
            {fileCategory === "image" ? (
              <img
                src={fileUrl}
                alt={fileName}
                className="w-full h-full object-cover"
              />
            ) : fileCategory === "video" ? (
              <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                <video
                  src={fileUrl}
                  className="w-full h-full object-cover opacity-60"
                />
                <Film className="absolute w-4 h-4 text-white" />
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <FileText className="w-4 h-4 text-indigo-600 mb-0.5" />
                <span className="text-[8px] font-bold uppercase text-gray-500 max-w-[32px] truncate">
                  {fileCategory}
                </span>
              </div>
            )}
          </div>

          {/* File Name & Details */}
          <div className="min-w-0 flex-1">
            <p
              className="text-xs font-semibold text-gray-800 truncate"
              title={fileName}
            >
              {fileName}
            </p>
            <p className="text-[11px] text-gray-400 mt-0.5">
              {formatFileSize(fileSize) || "Attached file"}
            </p>
          </div>
        </div>
        {/* Action Buttons */}
        <div className="flex items-center gap-1 shrink-0">
          {(fileCategory === "image" || fileCategory === "video") && (
            <button
              type="button"
              onClick={() =>
                setPreviewMedia({
                  url: fileUrl,
                  type: fileCategory,
                  name: fileName,
                })
              }
              className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
              title="Preview"
            >
              <Eye className="w-4 h-4" />
            </button>
          )}

          <a
            href={fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
            title="Open in new tab"
          >
            <ExternalLink className="w-4 h-4" />
          </a>

          <a
            href={fileUrl}
            download={fileName}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
            title="Download File"
          >
            <Download className="w-4 h-4" />
          </a>
        </div>
      </div>
      {/* Lightbox Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 z-[5001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewMedia(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>
            {previewMedia.type === "video" ? (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg"
              />
            ) : (
              <img
                src={previewMedia.url}
                alt={previewMedia.name || "Preview"}
                className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export const MultiFileDownloadView = ({ label, urls }) => {
  const [previewMedia, setPreviewMedia] = useState(null);

  if (!urls || !Array.isArray(urls)) return null;

  // Extract valid string URL regardless of data shape
  const getUrlString = (item) => {
    if (typeof item === "string") return item;
    if (typeof item === "object" && item !== null)
      return item.url || item.path || "";
    return "";
  };

  // Resolve display file name
  const getFileName = (item) => {
    if (typeof item === "object" && item !== null && item.name) {
      return item.name;
    }

    const urlStr = getUrlString(item);
    if (!urlStr) return "Download File";

    return urlStr.substring(urlStr.lastIndexOf("/") + 1) || "Download File";
  };

  // Resolve file extension category (image, video, document)
  const getFileType = (fileName, url) => {
    const target = (fileName || url || "").toLowerCase();
    if (
      /\.(apng|avif|gif|jpg|jpeg|jfif|pjpeg|pjp|png|svg|webp|bmp|ico)$/i.test(
        target,
      )
    ) {
      return "image";
    }
    if (/\.(mp4|webm|ogg|mov|m4v|mkv|avi)$/i.test(target)) {
      return "video";
    }
    const extMatch = target.match(/\.([a-z0-9]+)(\?.*)?$/i);
    return extMatch ? extMatch[1] : "file";
  };

  // Format file size if provided in object
  const formatFileSize = (bytes) => {
    if (!bytes || isNaN(bytes)) return null;
    if (bytes < 1024) return bytes + " B";
    if (bytes < 1048576) return (bytes / 1024).toFixed(1) + " KB";
    return (bytes / 1048576).toFixed(1) + " MB";
  };

  return (
    <>
      <label className="text-sm font-semibold text-gray-800 capitalize">
        {formatUnderscoreToSpace(label)}
      </label>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 w-full">
        {urls.map((item, index) => {
          const fileUrl = getUrlString(item);
          const fileName = getFileName(item);
          const fileSize = typeof item === "object" ? item.size : null;
          const fileCategory = getFileType(fileName, fileUrl);

          if (!fileUrl) return null;

          return (
            <div
              key={index}
              className="group relative rounded-xl border border-gray-200 bg-white p-3 shadow-xs transition hover:shadow-md flex items-center justify-between gap-3"
            >
              {/* Thumbnail and Info Section */}
              <div className="flex items-center gap-3 min-w-0 flex-1">
                {/* Preview Thumbnail / Icon */}
                <div className="relative flex-shrink-0 w-11 h-11 rounded-lg overflow-hidden border border-gray-100 bg-gray-50 flex items-center justify-center">
                  {fileCategory === "image" ? (
                    <img
                      src={fileUrl}
                      alt={fileName}
                      className="w-full h-full object-cover"
                    />
                  ) : fileCategory === "video" ? (
                    <div className="relative w-full h-full bg-gray-900 flex items-center justify-center">
                      <video
                        src={fileUrl}
                        className="w-full h-full object-cover opacity-60"
                      />
                      <Film className="absolute w-4 h-4 text-white" />
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center">
                      <FileText className="w-4 h-4 text-indigo-600 mb-0.5" />
                      <span className="text-[8px] font-bold uppercase text-gray-500 max-w-[32px] truncate">
                        {fileCategory}
                      </span>
                    </div>
                  )}
                </div>

                {/* File Name & Details */}
                <div className="min-w-0 flex-1">
                  <p
                    className="text-xs font-semibold text-gray-800 truncate"
                    title={fileName}
                  >
                    {fileName}
                  </p>
                  <p className="text-[11px] text-gray-400 mt-0.5">
                    {formatFileSize(fileSize) || "Attached file"}
                  </p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-1 shrink-0">
                {(fileCategory === "image" || fileCategory === "video") && (
                  <button
                    type="button"
                    onClick={() =>
                      setPreviewMedia({
                        url: fileUrl,
                        type: fileCategory,
                        name: fileName,
                      })
                    }
                    className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                    title="Preview"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                )}

                <a
                  href={fileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                  title="Open in new tab"
                >
                  <ExternalLink className="w-4 h-4" />
                </a>

                <a
                  href={fileUrl}
                  download={fileName}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-lg text-gray-500 hover:text-indigo-600 hover:bg-indigo-50 transition"
                  title="Download File"
                >
                  <Download className="w-4 h-4" />
                </a>
              </div>
            </div>
          );
        })}
      </div>
      {/* Lightbox Modal */}
      {previewMedia && (
        <div
          className="fixed inset-0 z-[5001] flex items-center justify-center bg-black/80 p-4 backdrop-blur-xs"
          onClick={() => setPreviewMedia(null)}
        >
          <div
            className="relative max-h-[90vh] max-w-[90vw] overflow-hidden rounded-xl bg-gray-900 shadow-2xl flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={() => setPreviewMedia(null)}
              className="absolute right-3 top-3 z-10 rounded-full bg-black/50 p-2 text-white hover:bg-black/70 transition"
            >
              <X className="w-5 h-5" />
            </button>
            {previewMedia.type === "video" ? (
              <video
                src={previewMedia.url}
                controls
                autoPlay
                className="max-h-[85vh] max-w-full rounded-lg"
              />
            ) : (
              <img
                src={previewMedia.url}
                alt={previewMedia.name || "Preview"}
                className="max-h-[85vh] max-w-full object-contain rounded-lg p-2"
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};
